import React, { useCallback, useEffect, useState } from "react";
import "./Content.css";
import {
  createBlogComment,
  createBlogPost,
  deleteBlogComment,
  deleteBlogPost,
  getBlogComments,
  getBlogPosts,
  updateBlogPost,
} from "../api";
import logger from "../logger";

const Content = () => {
  const [selectedContent, setSelectedContent] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newContent, setNewContent] = useState({
    title: "",
    body: "",
    image: null,
    imagePreview: null,
    animation: null,
  });
  const [errors, setErrors] = useState({});
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);
  const [deletingPostId, setDeletingPostId] = useState(null);
  const [blogComments, setBlogComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [deletingCommentId, setDeletingCommentId] = useState(null);

  const mapBlogToContent = (blog) => ({
    id: blog.id,
    name: blog.title || "Untitled",
    status:
      blog.is_published === true ||
      blog.is_published === 1 ||
      blog.is_published === "1"
        ? "Public"
        : "Draft",
    views: 0,
    change: 0,
    image: blog.file_url || blog.file || null,
    body: blog.content || "",
    likes: 0,
    comments: [],
    shares: 0,
    author: "Admin User",
    publishDate: blog.published_at
      ? new Date(blog.published_at).toISOString().split("T")[0]
      : blog.created_at
        ? new Date(blog.created_at).toISOString().split("T")[0]
        : "N/A",
    tags: [],
  });

  const fetchLatestBlogs = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getBlogPosts(1, 50, "all");
      const sorted = [...(result.data || [])].sort((a, b) => {
        const dateA = new Date(a.published_at || a.created_at || 0).getTime();
        const dateB = new Date(b.published_at || b.created_at || 0).getTime();
        return dateB - dateA;
      });
      const mapped = sorted.map(mapBlogToContent);
      setContents(mapped);
      if (mapped.length > 0) {
        setSelectedContent((prev) =>
          mapped.some((content) => content.id === prev) ? prev : mapped[0].id,
        );
      }
    } catch (err) {
      logger.error("Failed to fetch latest blogs from /blogs", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLatestBlogs();
  }, [fetchLatestBlogs]);

  const filteredContents = contents.filter((content) =>
    content.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const validateImage = (file, type, callback) => {
    if (!file) return;
    const validTypes =
      type === "image" ? ["image/png", "image/jpeg"] : ["image/gif"];
    if (!validTypes.includes(file.type)) {
      alert(`Please upload a ${type === "image" ? "PNG/JPG" : "GIF"} file.`);
      callback(null);
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      alert("File size must be 4MB or less.");
      callback(null);
      return;
    }
    callback(file);
  };

  const handleImageUpload = (e, type) => {
    const file = e.target.files[0];
    validateImage(file, type, (validFile) => {
      if (validFile) {
        setNewContent({
          ...newContent,
          [type]: validFile,
          ...(type === "image"
            ? { imagePreview: URL.createObjectURL(validFile) }
            : {}),
        });
      }
    });
  };

  const validateContent = () => {
    const newErrors = {};
    if (!newContent.title.trim()) newErrors.title = "Title is required";
    if (!newContent.body.trim()) newErrors.body = "Body is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setNewContent({
      title: "",
      body: "",
      image: null,
      imagePreview: null,
      animation: null,
    });
    setErrors({});
  };

  const openCreateModal = () => {
    setEditingPostId(null);
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (contentItem) => {
    setEditingPostId(contentItem.id);
    setNewContent({
      title: contentItem.name || "",
      body: contentItem.body || "",
      image: null,
      imagePreview: contentItem.image || null,
      animation: null,
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const handleDeleteContent = async (id) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;

    setDeletingPostId(id);
    try {
      await deleteBlogPost(id);
      await fetchLatestBlogs();
    } catch (err) {
      logger.error("Failed to delete blog post via /blogs", err);
      alert(err?.message || "Failed to delete post");
    } finally {
      setDeletingPostId(null);
    }
  };

  const handleCreateContent = async (e) => {
    e.preventDefault();
    if (!validateContent()) return;

    setSubmitting(true);
    try {
      const title = newContent.title.trim();
      const content = newContent.body.trim();
      const slug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      const currentPost = contents.find((item) => item.id === editingPostId);
      const isPublished = currentPost ? currentPost.status === "Public" : true;

      const payload = {
        blog_category_id: null,
        title,
        slug,
        excerpt: content.slice(0, 160),
        content,
        file: newContent.image || newContent.imagePreview || "",
        is_published: isPublished,
        published_at: new Date().toISOString(),
      };

      if (editingPostId) {
        await updateBlogPost(editingPostId, payload);
      } else {
        await createBlogPost(payload);
      }

      await fetchLatestBlogs();
      setEditingPostId(null);
      resetForm();
      setIsModalOpen(false);
    } catch (err) {
      logger.error("Failed to save blog post via /blogs", err);
      alert(err?.message || "Failed to save post");
    } finally {
      setSubmitting(false);
    }
  };

  const getShareLink = (platform, post) => {
    const baseUrl = window.location.origin;
    const postUrl = `${baseUrl}/blog?post=${post.id}`;
    const text = `${post.name} - CARE Blog`;

    if (platform === "Twitter") {
      return `https://twitter.com/intent/tweet?url=${encodeURIComponent(postUrl)}&text=${encodeURIComponent(text)}`;
    }

    if (platform === "Facebook") {
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}`;
    }

    if (platform === "LinkedIn") {
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(postUrl)}`;
    }

    if (platform === "WhatsApp") {
      return `https://wa.me/?text=${encodeURIComponent(`${text} ${postUrl}`)}`;
    }

    return postUrl;
  };

  const mapCommentToDisplay = (comment) => ({
    id:
      comment.id ||
      `${comment.created_at || ""}-${comment.comment || comment.content || ""}`,
    user: comment.full_name || "Anonymous",
    text: comment.comment || comment.content || comment.body || "",
    timestamp: comment.created_at
      ? new Date(comment.created_at).toISOString().split("T")[0]
      : "",
  });

  const fetchBlogComments = useCallback(async (blogId) => {
    if (!blogId) {
      setBlogComments([]);
      return;
    }

    setCommentsLoading(true);
    try {
      const result = await getBlogComments(blogId, 1, 100);
      const mappedComments = (result.data || []).map(mapCommentToDisplay);
      setBlogComments(mappedComments);
    } catch (err) {
      logger.error("Failed to fetch comments from /blog_comments", err);
      setBlogComments([]);
    } finally {
      setCommentsLoading(false);
    }
  }, []);

  const handleSubmitComment = async () => {
    if (!selectedContentData?.id || !commentText.trim()) return;

    setCommentSubmitting(true);
    try {
      const text = commentText.trim();
      await createBlogComment({
        blog_id: selectedContentData.id,
        comment: text,
      });

      setCommentText("");
      await fetchBlogComments(selectedContentData.id);
    } catch (err) {
      logger.error("Failed to create comment via /blog_comments", err);
      alert(err?.message || "Failed to submit comment");
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!selectedContentData?.id || !commentId) return;
    if (!window.confirm("Are you sure you want to delete this comment?")) return;

    setDeletingCommentId(commentId);
    try {
      await deleteBlogComment(commentId);
      await fetchBlogComments(selectedContentData.id);
    } catch (err) {
      logger.error("Failed to delete comment via /blog_comments/:id", err);
      alert(err?.message || "Failed to delete comment");
    } finally {
      setDeletingCommentId(null);
    }
  };

  const selectedContentData =
    contents.find((content) => content.id === selectedContent) || null;

  useEffect(() => {
    if (selectedContentData?.id) {
      fetchBlogComments(selectedContentData.id);
    } else {
      setBlogComments([]);
    }
    setCommentText("");
  }, [selectedContentData?.id, fetchBlogComments]);

  return (
    <div className="content-page">
      {/* Left Panel - Content List */}
      <div className="content-list-panel">
        <div className="content-header">
          <h1>Blog Posts</h1>
        </div>

        <div className="content-actions">
          <button className="btn-create-content" onClick={openCreateModal}>
            Create Post
          </button>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <svg
              className="search-icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
            </svg>
            {searchTerm && (
              <button
                className="clear-search"
                onClick={() => setSearchTerm("")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="content-list">
          {filteredContents.map((content) => (
            <div
              key={content.id}
              className={`content-card ${selectedContent === content.id ? "selected" : ""}`}
              onClick={() => setSelectedContent(content.id)}
            >
              {content.image ? (
                <img
                  src={content.image}
                  alt={content.name}
                  className="content-thumbnail"
                />
              ) : (
                <div className="content-thumbnail" />
              )}
              <div className="content-info">
                <h3 className="content-name">{content.name}</h3>
                <div className="content-meta">
                  <span
                    className={`status-badge ${content.status === "Public" ? "public" : "archived"}`}
                  >
                    {content.status}
                  </span>
                  <span className="views-value">{content.views} views</span>
                  <span className="change-indicator">+{content.change}</span>
                </div>
              </div>
              <div className="content-card-actions">
                <button
                  className="content-action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditModal(content);
                  }}
                >
                  Edit
                </button>
                <button
                  className="content-action-btn delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteContent(content.id);
                  }}
                  disabled={deletingPostId === content.id}
                >
                  {deletingPostId === content.id ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          ))}
          {filteredContents.length === 0 && (
            <div className="no-results">No posts found</div>
          )}
        </div>
      </div>

      {/* Right Panel - Post Details */}
      <div className="content-details-panel">
        <div className="details-header">
          <h2>Blog Details</h2>
        </div>

        {loading ? (
          <div className="no-results">Loading posts...</div>
        ) : !selectedContentData ? (
          <div className="no-results">No posts available.</div>
        ) : (
          <>
            <div className="blog-preview">
              {selectedContentData.image && (
                <img
                  src={selectedContentData.image}
                  alt={selectedContentData.name}
                  className="blog-thumbnail"
                />
              )}
              <h3>{selectedContentData.name}</h3>
              <div className="post-meta">
                <span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z" />
                  </svg>
                  {selectedContentData.publishDate}
                </span>
                <span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                  {selectedContentData.author}
                </span>
              </div>
              <p>{selectedContentData.body}</p>
              {selectedContentData.animation && (
                <img
                  src={selectedContentData.animation}
                  alt="Animation"
                  className="blog-animation"
                />
              )}
              <div className="tags-section">
                {selectedContentData.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="engagement-section">
              <h3>Share on Social Media</h3>
              <div className="social-share-buttons">
                <a
                  className="metric-btn social-share-btn m-2"
                  href={getShareLink("Twitter", selectedContentData)}
                  target="_blank"
                  rel="noreferrer"
                  title="Share on Twitter"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M18.244 2H21.5l-7.112 8.13L22 22h-5.956l-4.664-6.104L6.034 22H2.776l7.606-8.697L2 2h6.107l4.216 5.53L18.244 2zm-1.142 18h1.799L7.13 3.895H5.2L17.102 20z" />
                  </svg>
                  <span>Twitter</span>
                </a>
                <a
                  className="metric-btn social-share-btn m-2"
                  href={getShareLink("Facebook", selectedContentData)}
                  target="_blank"
                  rel="noreferrer"
                  title="Share on Facebook"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M13.5 9H16V6h-2.5C10.462 6 9 7.462 9 10.5V13H6v3h3v6h3v-6h3l1-3h-4v-2.5c0-.827.673-1.5 1.5-1.5z" />
                  </svg>
                  <span>Facebook</span>
                </a>
                <a
                  className="metric-btn social-share-btn"
                  href={getShareLink("LinkedIn", selectedContentData)}
                  target="_blank"
                  rel="noreferrer"
                  title="Share on LinkedIn"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M6.94 8.5A1.56 1.56 0 1 1 6.94 5.38a1.56 1.56 0 0 1 0 3.12zM5.5 9.75h2.88V19H5.5V9.75zM10.25 9.75H13v1.26h.04c.39-.74 1.34-1.52 2.75-1.52 2.94 0 3.48 1.9 3.48 4.37V19h-2.88v-4.5c0-1.08-.02-2.46-1.54-2.46-1.54 0-1.78 1.17-1.78 2.39V19h-2.88V9.75z" />
                  </svg>
                  <span>LinkedIn</span>
                </a>
                <a
                  className="metric-btn social-share-btn"
                  href={getShareLink("WhatsApp", selectedContentData)}
                  target="_blank"
                  rel="noreferrer"
                  title="Share on WhatsApp"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M19.11 4.89A9.9 9.9 0 0 0 12.06 2C6.56 2 2.1 6.46 2.1 11.96c0 1.77.46 3.5 1.35 5.02L2 22l5.15-1.35a9.94 9.94 0 0 0 4.9 1.26h.01c5.5 0 9.96-4.46 9.96-9.96a9.9 9.9 0 0 0-2.91-7.06zm-7.05 15.3h-.01a8.26 8.26 0 0 1-4.2-1.15l-.3-.17-3.06.8.82-2.98-.2-.31a8.24 8.24 0 0 1-1.27-4.42c0-4.55 3.7-8.26 8.25-8.26 2.2 0 4.27.86 5.83 2.42a8.19 8.19 0 0 1 2.42 5.84c0 4.55-3.7 8.25-8.25 8.25zm4.53-6.18c-.25-.13-1.49-.73-1.72-.82-.23-.08-.4-.13-.57.13-.17.25-.65.82-.8.99-.15.17-.29.19-.54.06-.25-.13-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.7-.14-.25-.02-.39.1-.52.11-.11.25-.29.38-.44.13-.15.17-.25.25-.42.08-.17.04-.31-.02-.44-.06-.13-.57-1.36-.78-1.87-.21-.5-.42-.43-.57-.44h-.49c-.17 0-.44.06-.67.31-.23.25-.88.86-.88 2.1s.9 2.43 1.02 2.6c.13.17 1.77 2.7 4.29 3.79.6.26 1.07.42 1.44.53.6.19 1.15.16 1.58.1.48-.07 1.49-.61 1.7-1.19.21-.58.21-1.08.15-1.19-.06-.11-.23-.17-.48-.29z" />
                  </svg>
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>

            <div className="comments-section">
              <h3>Comments</h3>
              {commentsLoading ? (
                <p className="no-comments">Loading comments...</p>
              ) : blogComments.length > 0 ? (
                <div className="comment-list">
                  {blogComments.map((comment) => (
                    <div key={comment.id} className="comment">
                      <div className="comment-header">
                        <span className="comment-user">{comment.user}</span>
                        <div className="comment-header-actions">
                          <span className="comment-timestamp">
                            {comment.timestamp}
                          </span>
                          <button
                            type="button"
                            className="comment-delete-btn"
                            onClick={() => handleDeleteComment(comment.id)}
                            disabled={deletingCommentId === comment.id}
                          >
                            {deletingCommentId === comment.id
                              ? "Deleting..."
                              : "Delete"}
                          </button>
                        </div>
                      </div>
                      <p>{comment.text}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-comments">No comments yet.</p>
              )}
              <div className="comment-input">
                <textarea
                  placeholder="Add a comment..."
                  className="form-textarea"
                  rows="3"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                />
                <button
                  type="button"
                  className="comment-submit-btn"
                  onClick={handleSubmitComment}
                  disabled={commentSubmitting || !commentText.trim()}
                >
                  {commentSubmitting ? "Submitting..." : "Submit Comment"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create Content Modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingPostId ? "Edit Post" : "Create New Post"}</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setIsModalOpen(false);
                  setEditingPostId(null);
                  resetForm();
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateContent}>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={newContent.title}
                  onChange={(e) =>
                    setNewContent({ ...newContent, title: e.target.value })
                  }
                  placeholder="Enter post title"
                  className="form-input"
                />
                {errors.title && (
                  <span className="error-message">{errors.title}</span>
                )}
              </div>
              <div className="form-group">
                <label>Body</label>
                <textarea
                  value={newContent.body}
                  onChange={(e) =>
                    setNewContent({ ...newContent, body: e.target.value })
                  }
                  placeholder="Enter post content"
                  className="form-textarea"
                  rows="6"
                />
                {errors.body && (
                  <span className="error-message">{errors.body}</span>
                )}
              </div>
              <div className="form-group">
                <label>Image (Optional)</label>
                <label className="upload-btn">
                  Upload Image
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    style={{ display: "none" }}
                    onChange={(e) => handleImageUpload(e, "image")}
                  />
                </label>
                {newContent.imagePreview && (
                  <img
                    src={newContent.imagePreview}
                    alt="Preview"
                    className="preview-image"
                  />
                )}
                {errors.image && (
                  <span className="error-message">{errors.image}</span>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingPostId(null);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={submitting}
                >
                  {submitting
                    ? editingPostId
                      ? "Saving..."
                      : "Creating..."
                    : editingPostId
                      ? "Save Changes"
                      : "Create Post"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Content;
